import { html } from "lit";
import { t } from "../../i18n/index.ts";
import type { AppViewState } from "../app-view-state.ts";
import { icons } from "../icons.ts";
import { normalizeBasePath } from "../navigation.ts";
import { agentLogoUrl } from "./agents-utils.ts";

function resolveErrorMessage(error: string | null): string | null {
  if (!error) {
    return null;
  }
  switch (error) {
    case "missing-fields":
      return t("login.account.missingFields");
    case "weak-password":
      return t("login.account.weakPassword");
    case "Invalid login credentials":
      return t("login.account.invalid");
    case "User already registered":
      return t("login.account.alreadyRegistered");
    default:
      return error;
  }
}

export function renderAccountLogin(state: AppViewState) {
  const basePath = normalizeBasePath(state.basePath ?? "");
  const faviconSrc = agentLogoUrl(basePath);
  const isSignup = state.accountAuthMode === "signup";
  const errorMessage = resolveErrorMessage(state.accountLoginError);
  const confirmation = state.accountSignupConfirmation === "confirm-email"
    ? t("login.account.confirmEmail")
    : null;
  const submit = () => {
    if (isSignup) {
      void state.accountSignup();
    } else {
      void state.accountLogin();
    }
  };
  const submitLabel = state.accountLoginBusy
    ? t("login.account.submitting")
    : isSignup
      ? t("login.account.signupSubmit")
      : t("login.account.submit");
  const heading = isSignup ? t("login.account.signupTitle") : t("login.account.title");
  const toggleLabel = isSignup ? t("login.account.toggleToLogin") : t("login.account.toggleToSignup");
  const toggleMode = isSignup ? "login" : "signup";

  return html`
    <div class="login-gate">
      <div class="login-gate__card">
        <div class="login-gate__header">
          <img class="login-gate__logo" src=${faviconSrc} alt="Octra" />
          <div class="login-gate__title">Octra</div>
          <div class="login-gate__sub">${heading}</div>
        </div>
        <div class="login-gate__form">
          <label class="field">
            <span>${t("login.account.emailLabel")}</span>
            <input
              type="email"
              autocomplete="username"
              spellcheck="false"
              .value=${state.accountLoginEmail}
              ?disabled=${state.accountLoginBusy}
              @input=${(e: Event) => {
                state.accountLoginEmail = (e.target as HTMLInputElement).value;
                if (state.accountLoginError) {
                  state.accountLoginError = null;
                }
              }}
              placeholder=${t("login.account.emailPlaceholder")}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key === "Enter") {
                  submit();
                }
              }}
            />
          </label>
          <label class="field">
            <span>${t("login.account.passwordLabel")}</span>
            <div class="login-gate__secret-row">
              <input
                type=${state.accountLoginShowPassword ? "text" : "password"}
                autocomplete=${isSignup ? "new-password" : "current-password"}
                spellcheck="false"
                .value=${state.accountLoginPassword}
                ?disabled=${state.accountLoginBusy}
                @input=${(e: Event) => {
                  state.accountLoginPassword = (e.target as HTMLInputElement).value;
                  if (state.accountLoginError) {
                    state.accountLoginError = null;
                  }
                }}
                placeholder=${t("login.account.passwordPlaceholder")}
                @keydown=${(e: KeyboardEvent) => {
                  if (e.key === "Enter") {
                    submit();
                  }
                }}
              />
              <button
                type="button"
                class="btn btn--icon ${state.accountLoginShowPassword ? "active" : ""}"
                title=${state.accountLoginShowPassword
                  ? t("login.hidePassword")
                  : t("login.showPassword")}
                aria-label=${t("login.togglePasswordVisibility")}
                aria-pressed=${state.accountLoginShowPassword}
                @click=${() => {
                  state.accountLoginShowPassword = !state.accountLoginShowPassword;
                }}
              >
                ${state.accountLoginShowPassword ? icons.eye : icons.eyeOff}
              </button>
            </div>
          </label>
          <button
            class="btn primary login-gate__connect"
            ?disabled=${state.accountLoginBusy}
            @click=${submit}
          >
            ${submitLabel}
          </button>
          <button
            type="button"
            class="btn btn--ghost login-gate__toggle"
            ?disabled=${state.accountLoginBusy}
            @click=${() => state.setAccountAuthMode(toggleMode)}
          >
            ${toggleLabel}
          </button>
        </div>
        ${errorMessage
          ? html`
              <div
                class="callout danger login-gate__failure"
                role="alert"
                aria-live="polite"
                data-kind="auth-failed"
              >
                <div class="login-gate__failure-summary">${errorMessage}</div>
              </div>
            `
          : ""}
        ${confirmation
          ? html`
              <div
                class="callout login-gate__failure"
                role="status"
                aria-live="polite"
                data-kind="confirm-email"
              >
                <div class="login-gate__failure-summary">${confirmation}</div>
              </div>
            `
          : ""}
      </div>
    </div>
  `;
}
